import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useRouter } from "@tanstack/react-router";
import { motion } from "motion/react";
import {
  Copy,
  Check,
  Trash2,
  ExternalLink,
  MoreHorizontal,
  ToggleLeft,
  ToggleRight,
  MousePointerClick,
  Lock,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { deleteLinkFn, updateLinkFn } from "@/server/functions/links";
import { LinkDetailModal } from "./link-detail-modal";
import type { Links } from "@/server/lib/appwrite.types";

const SHORT_DOMAIN = "l.zugl.in";

interface DashboardLinksTableProps {
  links: Links[];
}

export function DashboardLinksTable({ links }: DashboardLinksTableProps) {
  const router = useRouter();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedLink, setSelectedLink] = useState<Links | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const deleteLink = useServerFn(deleteLinkFn);
  const updateLink = useServerFn(updateLinkFn);

  const handleCopy = async (shortCode: string, id: string) => {
    const url = `https://${SHORT_DOMAIN}/${shortCode}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      await deleteLink({ data: { id: deleteId } });
      toast.success("Link deleted");
      void router.invalidate();
    } catch {
      toast.error("Failed to delete link");
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await updateLink({ data: { id, isActive: !currentStatus } });
      toast.success(currentStatus ? "Link disabled" : "Link enabled");
      void router.invalidate();
    } catch {
      toast.error("Failed to update link");
    }
  };

  const handleViewDetails = (link: Links) => {
    setSelectedLink(link);
    setDetailModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (links.length === 0) {
    return (
      <div className="text-center py-16">
        <pre className="font-mono text-[#9B87F5]/30 text-xs mb-4 inline-block">
          {`┌─────────────────────────┐
│                         │
│    NO_LINKS_FOUND       │
│                         │
│    Create your first    │
│    link to get started  │
│                         │
└─────────────────────────┘`}
        </pre>
        <p className="text-[#8E9196] font-mono text-sm">
          Click "NEW_LINK" to create your first short link
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#9B87F5]/10">
              <th className="text-left py-3 px-4 text-xs font-mono text-[#8E9196]">
                SHORT_LINK
              </th>
              <th className="text-left py-3 px-4 text-xs font-mono text-[#8E9196] hidden md:table-cell">
                DESTINATION
              </th>
              <th className="text-center py-3 px-4 text-xs font-mono text-[#8E9196]">
                CLICKS
              </th>
              <th className="text-center py-3 px-4 text-xs font-mono text-[#8E9196] hidden sm:table-cell">
                STATUS
              </th>
              <th className="text-left py-3 px-4 text-xs font-mono text-[#8E9196] hidden lg:table-cell">
                CREATED
              </th>
              <th className="text-right py-3 px-4 text-xs font-mono text-[#8E9196]">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {links.map((link, index) => (
              <motion.tr
                key={link.$id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-[#9B87F5]/5 hover:bg-[#9B87F5]/5 transition-colors"
              >
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleViewDetails(link)}
                      className="text-[#9B87F5] hover:text-white font-mono text-sm transition-colors text-left"
                    >
                      {SHORT_DOMAIN}/{link.shortCode}
                    </button>
                    <button
                      onClick={() => handleCopy(link.shortCode, link.$id)}
                      className="p-1 hover:bg-[#9B87F5]/10 rounded transition-colors"
                    >
                      {copiedId === link.$id ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3 text-[#8E9196]" />
                      )}
                    </button>
                    {link.password && (
                      <Lock className="w-3 h-3 text-[#9B87F5]" />
                    )}
                  </div>
                  {link.title && (
                    <p className="text-xs text-[#8E9196] font-mono mt-1 truncate max-w-[200px]">
                      {link.title}
                    </p>
                  )}
                </td>
                <td className="py-4 px-4 hidden md:table-cell">
                  <a
                    href={link.originalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#8E9196] hover:text-white transition-colors flex items-center gap-1 font-mono text-xs max-w-[250px] truncate"
                  >
                    <span className="truncate">{link.originalUrl}</span>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </td>
                <td className="py-4 px-4 text-center">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#9B87F5]/10 text-[#9B87F5] font-mono text-sm">
                    <MousePointerClick className="w-3 h-3" />
                    {link.clicks}
                  </span>
                </td>
                <td className="py-4 px-4 text-center hidden sm:table-cell">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded font-mono text-xs ${
                      link.isActive
                        ? "bg-green-500/10 text-green-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    {link.isActive ? "ACTIVE" : "DISABLED"}
                  </span>
                </td>
                <td className="py-4 px-4 text-[#8E9196] font-mono text-xs hidden lg:table-cell">
                  {formatDate(link.$createdAt)}
                </td>
                <td className="py-4 px-4 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-[#8E9196] hover:text-white hover:bg-[#9B87F5]/10"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-[#1A1F2C] border-[#9B87F5]/20 font-mono"
                    >
                      <DropdownMenuItem
                        onClick={() => handleViewDetails(link)}
                        className="text-[#8E9196] hover:text-white focus:text-white focus:bg-[#9B87F5]/10"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        VIEW_DETAILS
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleCopy(link.shortCode, link.$id)}
                        className="text-[#8E9196] hover:text-white focus:text-white focus:bg-[#9B87F5]/10"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        COPY_LINK
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#9B87F5]/10" />
                      <DropdownMenuItem
                        onClick={() =>
                          handleToggleActive(link.$id, link.isActive)
                        }
                        className="text-[#8E9196] hover:text-white focus:text-white focus:bg-[#9B87F5]/10"
                      >
                        {link.isActive ? (
                          <>
                            <ToggleLeft className="w-4 h-4 mr-2" />
                            DISABLE
                          </>
                        ) : (
                          <>
                            <ToggleRight className="w-4 h-4 mr-2" />
                            ENABLE
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#9B87F5]/10" />
                      <DropdownMenuItem
                        onClick={() => setDeleteId(link.$id)}
                        className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        DELETE
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1A1F2C] border-[#9B87F5]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-mono">
              DELETE_LINK?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#8E9196] font-mono">
              This action cannot be undone. The short link will stop working
              immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#9B87F5]/20 text-[#8E9196] hover:bg-[#9B87F5]/10 hover:text-white font-mono">
              CANCEL
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white font-mono"
            >
              {isDeleting ? "DELETING..." : "DELETE"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Detail Modal */}
      <LinkDetailModal
        link={selectedLink}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onUpdate={() => router.invalidate()}
      />
    </>
  );
}
